import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ClubsReport from '../components/ClubsReport';
import {
  ClockIcon,
  TicketIcon,
  ArrowUpIcon,
  ChartBarIcon,
  CalendarIcon,
  BuildingStorefrontIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

interface Operator {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
}

interface OperatorReport {
  operator: Operator;
  period: {
    startDate: string;
    endDate: string;
  };
  metrics: {
    hoursWorked: number;
    ticketsCompleted: number;
    actualWorkTime: number; // минуты
    escalationsToSecondLine: number;
  };
  tickets: Array<{
    ticketNumber: string;
    status: string;
    resolutionTime: string;
    createdAt: string;
    workTime: number;
  }>;
}

const OperatorReportsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [view, setView] = useState<'operators' | 'clubs'>('operators');
  const [operators, setOperators] = useState<Operator[]>([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [report, setReport] = useState<OperatorReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'superadmin') {
      fetchOperators();
    }
  }, [user]);

  const fetchOperators = async () => {
    try {
      setLoadingOperators(true);
      const response = await api.get('/api/users', {
        params: {
          role: 'operator'
        }
      });
      
      const users = response.data.data?.users || response.data.data || [];
      setOperators(users);
      
      // Если оператор не выбран, выбираем первого
      if (!selectedOperatorId && users.length > 0) {
        setSelectedOperatorId(users[0].id);
      }
    } catch (error: any) {
      toast.error('Ошибка загрузки списка операторов');
    } finally {
      setLoadingOperators(false);
    }
  };

  const fetchReport = async () => {
    if (!selectedOperatorId) {
      toast.error('Выберите оператора');
      return;
    }

    if (!startDate || !endDate) {
      toast.error('Укажите период');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Дата начала не может быть позже даты окончания');
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/api/reports/operator/${selectedOperatorId}`, {
        params: {
          startDate,
          endDate
        }
      });

      if (response.data.success) {
        setReport(response.data.data);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки отчета');
    } finally {
      setLoading(false);
    }
  };

  // Формат HH:mm (округляем до целых минут — без уродливых float).
  const formatDuration = (minutes: number) => {
    if (!minutes || isNaN(minutes) || minutes < 0) return '00:00';
    const total = Math.round(minutes);
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch {
      return dateString;
    }
  };

  // Проверяем права доступа
  if (user?.role !== 'admin' && user?.role !== 'superadmin') {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-foreground">Нет доступа</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Эта страница доступна только администраторам
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <ChartBarIcon className="h-6 w-6" />
          Отчёты
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Статистика по операторам и аналитика поддержки по клубам
        </p>
      </div>

      {/* Табы */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setView('operators')}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
            view === 'operators' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <UsersIcon className="h-4 w-4" /> По операторам
        </button>
        <button
          onClick={() => setView('clubs')}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
            view === 'clubs' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <BuildingStorefrontIcon className="h-4 w-4" /> По клубам
        </button>
      </div>

      {view === 'clubs' ? <ClubsReport /> : (
      <>
      {/* Фильтры */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Оператор
            </label>
            {loadingOperators ? (
              <div className="text-sm text-muted-foreground">Загрузка...</div>
            ) : (
              <select
                value={selectedOperatorId}
                onChange={(e) => setSelectedOperatorId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="">Выберите оператора</option>
                {operators.map(operator => (
                  <option key={operator.id} value={operator.id}>
                    {operator.firstName} {operator.lastName} (@{operator.username})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Дата начала
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Дата окончания
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="flex items-end">
            <Button
              onClick={fetchReport}
              disabled={loading || !selectedOperatorId}
              className="w-full"
            >
              <CalendarIcon className="h-4 w-4 mr-2" />
              {loading ? 'Загрузка...' : 'Сформировать отчет'}
            </Button>
          </div>
        </div>
      </div>

      {/* Отчет */}
      {loading && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Формирование отчета...</p>
          </div>
        </div>
      )}

      {!loading && report && (
        <div className="space-y-6">
          {/* Метрики */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <ClockIcon className="h-5 w-5 text-muted-foreground" />
                <div className="text-xs text-muted-foreground">Часов отработано</div>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {report.metrics.hoursWorked.toFixed(1)}
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TicketIcon className="h-5 w-5 text-muted-foreground" />
                <div className="text-xs text-muted-foreground">Заявок выполнено</div>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {report.metrics.ticketsCompleted}
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <ClockIcon className="h-5 w-5 text-muted-foreground" />
                <div className="text-xs text-muted-foreground">Фактическое время работы</div>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {formatDuration(report.metrics.actualWorkTime)}
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpIcon className="h-5 w-5 text-muted-foreground" />
                <div className="text-xs text-muted-foreground">Обращений на 2-ю линию</div>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {report.metrics.escalationsToSecondLine}
              </div>
            </div>
          </div>

          {/* Список заявок */}
          {report.tickets && report.tickets.length > 0 && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border">
                <h3 className="text-lg font-medium text-foreground">
                  Заявки за период ({report.tickets.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Номер</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Статус</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Создано</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Решено</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Время работы</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {report.tickets.map((ticket, index) => (
                      <tr key={index} className="hover:bg-muted/30">
                        <td className="px-4 py-3 text-sm font-medium text-foreground">
                          {ticket.ticketNumber}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-1 rounded bg-muted text-foreground">
                            {ticket.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {formatDate(ticket.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {ticket.resolutionTime ? formatDate(ticket.resolutionTime) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {formatDuration(ticket.workTime / 60)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && !report && selectedOperatorId && (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <ChartBarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Выберите период и нажмите "Сформировать отчет"</p>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default OperatorReportsPage;

